import { Box, FormHelperText, FormLabel, useTheme } from "@mui/material";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import type { RichTextInputProps } from "./RichTextInput";
import { RichTextInput } from "./RichTextInput";

export interface FormRichTextInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<RichTextInputProps, "onChange" | "value"> {
  name: TName;
  control: Control<TFieldValues>;
  label?: string;
  required?: boolean;
}

export const FormRichTextInput = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  label,
  required,
  sx,
  ...props
}: FormRichTextInputProps<TFieldValues, TName>) => {
  const { colors, typography, customFontWeight, customSpacing, radius } =
    useTheme();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: customSpacing.md,
            ...sx,
          }}
        >
          {label && (
            <FormLabel
              htmlFor={props.id || name}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: typography.body1.fontSize,
                fontWeight: customFontWeight.bold,
                color: colors.text.primary,
                "&.Mui-focused": {
                  color: colors.text.primary,
                },
                "&.Mui-error": {
                  color: colors.text.primary,
                },
              }}
            >
              {label}
              {required && (
                <span
                  style={{
                    color: colors.text.danger,
                    fontSize: typography.body2.fontSize,
                    fontWeight: customFontWeight.medium,
                  }}
                >
                  *
                </span>
              )}
            </FormLabel>
          )}
          <RichTextInput
            {...props}
            {...field}
            id={props.id || name}
            onChange={(value: string) => field.onChange(value)}
            attributes={{
              class: error ? "input-error" : undefined,
            }}
          />
          {error && (
            <FormHelperText sx={{ color: colors.text.danger }}>
              {error.message}
            </FormHelperText>
          )}
          <style>
            {`.input-error {
            border: 2px solid ${colors.border.danger} !important;
            border-radius: ${radius.md};
          }`}
          </style>
        </Box>
      )}
    />
  );
};
